import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useResolveApproval, type ApprovalItem } from '@/hooks/useApprovals';

interface ApprovalCardProps {
  item: ApprovalItem;
  onOpen?: () => void;
}

/**
 * Relative time using Intl.RelativeTimeFormat — avoids hand-rolled i18n keys
 * for every bucket (minutes/hours/days). Language follows i18next.
 */
function formatRelative(iso: string, lang: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60_000);
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
  if (mins < 1) return rtf.format(0, 'minute');
  if (mins < 60) return rtf.format(-mins, 'minute');
  const hours = Math.round(mins / 60);
  if (hours < 24) return rtf.format(-hours, 'hour');
  const days = Math.round(hours / 24);
  if (days < 30) return rtf.format(-days, 'day');
  const months = Math.round(days / 30);
  return rtf.format(-months, 'month');
}

export function ApprovalCard({ item, onOpen }: ApprovalCardProps) {
  const { t, i18n } = useTranslation();
  const resolve = useResolveApproval();
  const isProcessing = resolve.isPending && resolve.variables?.id === item.id;
  const resolved = item.resolution;

  function onCardClick(e: MouseEvent<HTMLElement>) {
    // Ignore clicks bubbled from the action buttons
    if ((e.target as HTMLElement).closest('.appr-btn')) return;
    onOpen?.();
  }

  function onAction(action: 'approved' | 'rejected') {
    let comment: string | undefined;
    if (action === 'rejected') {
      const input = window.prompt(t('approvals.prompt_reject_reason'));
      if (input === null) return;
      comment = input.trim() || t('approvals.default_reject_reason');
    }
    resolve.mutate(
      comment !== undefined ? { id: item.id, action, comment } : { id: item.id, action },
      {
        onSuccess: () => {
          toast.success(
            action === 'approved' ? t('approvals.toast_approved') : t('approvals.toast_rejected'),
            { description: item.title },
          );
        },
        onError: () => {
          toast.error(t('approvals.toast_error'));
        },
      },
    );
  }

  return (
    <article
      className="appr-card"
      data-resolved={resolved ?? null}
      onClick={onCardClick}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
    >
      <div className="appr-stripe" data-priority={item.priority} />
      <div className="appr-card-body">
        <div className="appr-card-header">
          <div className="appr-type-icon" style={{ background: item.iconBgVar }}>
            <span
              className="material-symbols-outlined"
              style={{ color: item.iconColorVar }}
            >
              {item.icon}
            </span>
          </div>
          <div className="appr-card-header-body">
            <div className="appr-type-label" style={{ color: item.iconColorVar }}>
              {t(item.typeKey)}
            </div>
            <div className="appr-title">{item.title}</div>
            <div className="appr-time">{formatRelative(item.createdAt, i18n.language)}</div>
          </div>
        </div>

        <div className="appr-requester">
          <div
            className="req-avatar"
            style={{
              background: `linear-gradient(135deg, ${item.iconColorVar}, ${item.iconColorVar})`,
            }}
          >
            {item.requesterInitials}
          </div>
          <div className="req-info">
            <div className="req-name">{item.requesterName}</div>
            <div className="req-dept">{item.requesterDept}</div>
          </div>
          <div className="req-amount">{item.amount}</div>
        </div>

        <div className="appr-actions">
          <button
            type="button"
            className="appr-btn"
            data-variant="approve"
            disabled={isProcessing || Boolean(resolved)}
            onClick={() => onAction('approved')}
          >
            <span className="material-symbols-outlined">check_circle</span>
            {resolved === 'approved' ? t('approvals.approved') : t('approvals.approve')}
          </button>
          <button
            type="button"
            className="appr-btn"
            data-variant="reject"
            disabled={isProcessing || Boolean(resolved)}
            onClick={() => onAction('rejected')}
          >
            <span className="material-symbols-outlined">cancel</span>
            {resolved === 'rejected' ? t('approvals.rejected') : t('approvals.reject')}
          </button>
        </div>
      </div>
    </article>
  );
}
